import React, { useState, useEffect } from 'react';
import { Box, Container, Typography, TextField, Button } from '@mui/material';
import { styled } from '@mui/material/styles';
import Lottie from 'lottie-react';

const AnimationContainer = styled(Box)(({ theme }) => ({
  background: 'linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)',
  paddingTop: theme.spacing(15),
  paddingBottom: theme.spacing(1.5),
  paddingLeft: 0,
  paddingRight: 0,
  position: 'relative',
  overflow: 'hidden',
  width: '100vw',
  marginLeft: 'calc(-50vw + 50%)',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'radial-gradient(circle at 20% 50%, rgba(69, 91, 65, 0.05) 0%, transparent 50%)',
    pointerEvents: 'none'
  },
  [theme.breakpoints.down('md')]: {
    paddingTop: theme.spacing(10),
    paddingBottom: theme.spacing(1.2)
  },
  [theme.breakpoints.down('sm')]: {
    paddingTop: theme.spacing(8),
    paddingBottom: theme.spacing(1)
  }
}));

const WaveTop = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  height: '120px',
  zIndex: 10,
  overflow: 'hidden',
  '& svg': {
    width: '100%',
    height: '100%',
    display: 'block'
  },
  [theme.breakpoints.down('lg')]: {
    height: '100px'
  },
  [theme.breakpoints.down('md')]: {
    height: '80px'
  },
  [theme.breakpoints.down('sm')]: {
    height: '60px'
  },
  '@media (max-width: 480px)': {
    height: '50px'
  }
}));

const AnimationContent = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: theme.spacing(2),
  position: 'relative',
  zIndex: 1,
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
    textAlign: 'center',
    gap: theme.spacing(3)
  }
}));

const AnimationText = styled(Box)(({ theme }) => ({
  flex: '1 1 45%',
  maxWidth: '500px',
  transform: 'translateX(-30px)',
  [theme.breakpoints.down('md')]: {
    flex: '1 1 100%',
    maxWidth: '600px',
    transform: 'none'
  }
}));

const AnimationBox = styled(Box)(({ theme }) => ({
  flex: '0 0 600px',
  height: '500px',
  display: 'flex',
  alignItems: 'flex-end',
  justifyContent: 'center',
  marginTop: '-100px',
  [theme.breakpoints.down('md')]: {
    flex: '0 0 450px',
    height: '400px',
    order: -1,
    alignItems: 'center',
    marginTop: 0
  },
  [theme.breakpoints.down('sm')]: {
    flex: '0 0 350px',
    height: '300px',
    alignItems: 'center',
    marginTop: 0
  }
}));

const EmailForm = styled('form')(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(2),
  marginTop: theme.spacing(3),
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    gap: theme.spacing(1.5)
  }
}));

const EmailInput = styled(TextField)(({ theme }) => ({
  flex: 1,
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#ffffff',
    borderRadius: '30px',
    fontSize: '1rem',
    '& fieldset': {
      borderColor: 'rgba(69, 91, 65, 0.3)',
    },
    '&:hover fieldset': {
      borderColor: '#455b41',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#455b41',
      borderWidth: '2px'
    },
  },
  '& .MuiInputBase-input': {
    padding: '14px 20px',
  },
  [theme.breakpoints.down('sm')]: {
    '& .MuiInputBase-input': {
      padding: '12px 16px',
    }
  }
}));

const CTAButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#455b41',
  color: '#ffffff',
  padding: '14px 32px',
  fontSize: '1.1rem',
  fontWeight: 600,
  borderRadius: '30px',
  textTransform: 'none',
  transition: 'all 0.3s ease',
  boxShadow: '0 4px 15px rgba(69, 91, 65, 0.2)',
  whiteSpace: 'nowrap',
  '&:hover': {
    backgroundColor: '#5a7054',
    transform: 'translateY(-2px)',
    boxShadow: '0 6px 20px rgba(69, 91, 65, 0.3)'
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '1rem',
    padding: '12px 24px'
  }
}));

const AnimationSection: React.FC = () => {
  const [animationData, setAnimationData] = useState<any>(null);
  const [email, setEmail] = useState('');

  useEffect(() => {
    fetch('/farmer-animation.json')
      .then(response => response.json())
      .then(data => setAnimationData(data))
      .catch(error => console.error('Error loading animation:', error));
  }, []);

  const handleEmailSubscription = (e: React.FormEvent) => {
    e.preventDefault();
    if (email) {
      console.log('Subscribing email:', email);
      alert(`¡Gracias por suscribirte! Te hemos enviado un correo de confirmación a ${email}`);
      setEmail('');
    }
  };

  return (
    <AnimationContainer>
      <WaveTop>
        <svg viewBox="0 0 1200 120" preserveAspectRatio="none">
          <path 
            d="M0,120 C150,20 300,120 600,70 C900,20 1050,120 1200,70 L1200,0 L0,0 Z" 
            fill="#f4f4f0"
          />
        </svg>
      </WaveTop>
      
      <Container maxWidth="lg">
        <AnimationContent>
          <AnimationText>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 700,
                color: '#2a3a28',
                fontSize: { xs: '1.8rem', sm: '2.2rem', md: '2.5rem' },
                marginBottom: 1,
                fontFamily: '"Lora", serif',
                lineHeight: 1.2
              }}
            >
              Productos orgánicos directamente del agricultor
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: '#5a5a5a',
                fontSize: { xs: '1rem', sm: '1.1rem' },
                lineHeight: 1.6,
                marginBottom: 2
              }}
            >
              Únete a nuestra comunidad de más de 10,000 suscriptores y recibe las mejores 
              ofertas y consejos sobre agricultura sostenible.
            </Typography>
            <EmailForm onSubmit={handleEmailSubscription}>
              <EmailInput
                type="email"
                placeholder="Ingresa tu correo electrónico"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                variant="outlined"
              />
              <CTAButton
                type="submit"
                variant="contained"
              >
                Suscríbete
              </CTAButton>
            </EmailForm>
          </AnimationText>
          
          <AnimationBox>
            {animationData ? (
              <Lottie
                animationData={animationData}
                loop={true}
                autoplay={true}
                style={{ 
                  width: '100%', 
                  height: '100%',
                  transform: 'scale(1.5)',
                  transformOrigin: 'center',
                  marginTop: '-80px'
                }}
              />
            ) : (
              <Box
                sx={{
                  width: { xs: '300px', sm: '400px', md: '500px' },
                  height: { xs: '300px', sm: '400px', md: '500px' },
                  borderRadius: '50%',
                  backgroundColor: 'rgba(69, 91, 65, 0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: { xs: '120px', sm: '160px', md: '200px' }
                }}
              >
                🌾
              </Box>
            )}
          </AnimationBox>
        </AnimationContent>
      </Container>
    </AnimationContainer>
  );
};

export default AnimationSection;